import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService, OAuthEvent } from 'angular-oauth2-oidc';
import { authConfig, getAuthConfig, AuthProvider } from './auth.config';
import { BehaviorSubject, Observable, filter } from 'rxjs';

export interface UserProfile {
  sub?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  preferred_username?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private oauthService = inject(OAuthService);
  private router = inject(Router);
  
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  
  private userProfileSubject = new BehaviorSubject<UserProfile | null>(null);
  public userProfile$ = this.userProfileSubject.asObservable();
  
  private tokenExpirationCheckInterval: any;
  private discoveryDocumentLoaded: Promise<void> | null = null;
  private _silentLoginFailed = false;

  /** True when silent login (SSO cookie) was attempted and failed */
  public get silentLoginFailed(): boolean {
    return this._silentLoginFailed;
  }

  constructor() {
    this.configure();
    this.setupEventHandlers();
    this.startTokenExpirationCheck();
  }

  /**
   * Konfiguracia OAuth servisu
   * 
   * Flow:
   * 1. Load discovery document & try to process any code/error in URL
   * 2. If tokens valid -> authenticated
   * 3. If URL has error (failed silent login) -> mark failed, show login page
   * 4. If no tokens and silent login not yet attempted -> try silent login (prompt=none)
   * 5. If silent login already attempted -> show login page
   */
  private configure() {
    console.log('Konfigurujem OAuth service...');

    // Detect provider from the current URL path (e.g. /login/ardaco → 'ardaco').
    // This is the authoritative source when the user navigates to a specific calling-line URL.
    const urlPath = window.location.pathname;
    const urlProviderMatch = urlPath.match(/\/login\/([a-z]+)/);
    const urlProvider = urlProviderMatch ? (urlProviderMatch[1] as AuthProvider) : null;
    if (urlProvider) {
      console.log('Detected provider from URL:', urlProvider);
    }

    // Restore provider saved before the OIDC redirect, so the callback is
    // processed against the correct issuer/clientId.
    // URL provider takes precedence over the saved value (handles the case where
    // the calling line specifies a different provider than the last login session).
    const savedProvider = sessionStorage.getItem('auth_provider') as AuthProvider | null;
    const provider: AuthProvider = urlProvider || savedProvider || 'default';
    if (!urlProvider && savedProvider) {
      console.log('Detected saved provider from login redirect:', savedProvider);
    }

    const config = getAuthConfig(provider);
    console.log('Issuer:', config.issuer);
    console.log('Client ID:', config.clientId);
    console.log('Redirect URI:', config.redirectUri);
    
    this.oauthService.configure(config);
    this.oauthService.setupAutomaticSilentRefresh();
    
    // Nacitanie OIDC discovery dokumentu a pokus o prihlasenie
    console.log('Nacitavam OIDC discovery document...');
    this.discoveryDocumentLoaded = this.oauthService.loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        console.log('Discovery document nacitany uspesne');
        console.log('Kontrolujem token...');
        console.log('   - hasValidAccessToken:', this.oauthService.hasValidAccessToken());
        console.log('   - hasValidIdToken:', this.oauthService.hasValidIdToken());
        console.log('   - Access Token:', this.oauthService.getAccessToken());
        console.log('   - Refresh Token:', this.oauthService.getRefreshToken() ? 'Present' : 'Missing');
        
        if (this.oauthService.hasValidAccessToken()) {
          // Tokens are valid - user is authenticated
          console.log('Platny access token najdeny');
          this.isAuthenticatedSubject.next(true);
          this.loadUserProfile();
          sessionStorage.removeItem('auth_provider');
          return;
        }
        
        // No valid tokens - check if Keycloak returned an error
        // (e.g. login_required from a failed silent login triggered by AppComponent)
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('error')) {
          console.log('OAuth error in URL:', urlParams.get('error'), urlParams.get('error_description'));
          this._silentLoginFailed = true;
          // Clean error params from URL
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
        
        // No tokens and no error - silent login will be triggered by AppComponent
        // via NavigationEnd once Angular routing has settled.
        console.log('Ziadny platny access token, cakam na navigaciu...');
      }).catch(err => {
        console.error('Chyba pri nacitani OIDC konfigurace:', err);
        console.error('Detail chyby:', err.message || err);
        this._silentLoginFailed = true;
      });
  }

  /**
   * Nastavenie event handlerov pre OAuth events
   */
  private setupEventHandlers() {
    // Subscribe to important OAuth events only (filtered to reduce noise)
    this.oauthService.events.subscribe((e: OAuthEvent) => {
      // Only log important events, skip noisy session checks
      const importantEvents = [
        'token_received', 
        'token_error', 
        'code_error', 
        'silent_refresh_error', 
        'silent_refresh_timeout',
        'token_refresh_error',
        'logout',
        'session_terminated',
        'discovery_document_loaded',
        'discovery_document_load_error'
      ];
      
      if (importantEvents.includes(e.type)) {
        console.log('OAuth Event:', e.type, e);
      }
      
      // Special handling pre token_error
      if (e.type === 'token_error') {
        console.error('❌ Token Error:', e);
        console.error('Token Error Details:', JSON.stringify(e, null, 2));
      }
      
      // Special handling pre code_error
      if (e.type === 'code_error') {
        console.error('❌ Code Error (authorization code exchange failed):', e);
        console.error('Code Error Details:', JSON.stringify(e, null, 2));
      }
      
      // Handle discovery document load errors
      if (e.type === 'discovery_document_load_error') {
        console.error('❌ Discovery Document Load Error:', e);
      }
      
      // Handle silent refresh errors (SSO session expired after 2 minutes)
      if (e.type === 'silent_refresh_error' || e.type === 'silent_refresh_timeout') {
        console.error('Silent refresh failed - SSO session likely expired (2 min limit)');
        console.log('User will need to re-authenticate on next action');
        // Clear tokens since they're expired and can't be refreshed
        this.oauthService.logOut(false); // Don't redirect to Keycloak logout
        this.isAuthenticatedSubject.next(false);
      }
      
      // Handle token refresh failure
      if (e.type === 'token_refresh_error') {
        console.error('Token refresh failed - SSO session expired');
        this.oauthService.logOut(false);
        this.isAuthenticatedSubject.next(false);
      }
    });
    
    this.oauthService.events
      .pipe(filter((e: OAuthEvent) => e.type === 'token_received'))
      .subscribe(() => {
        console.log('Token received event!');
        this.isAuthenticatedSubject.next(true);
        this.loadUserProfile();
        
        // Vypis tokenu
        const accessToken = this.oauthService.getAccessToken();
        const idToken = this.oauthService.getIdToken();
        console.log('Access Token:', accessToken);
        console.log('ID Token:', idToken);
        console.log('Token expires at:', new Date(this.oauthService.getAccessTokenExpiration()));
        
        // Dekodovany obsah ID tokenu
        const claims = this.oauthService.getIdentityClaims();
        console.log('User Claims:', claims);
      });

    this.oauthService.events
      .pipe(filter((e: OAuthEvent) => e.type === 'logout'))
      .subscribe(() => {
        this.isAuthenticatedSubject.next(false);
        this.userProfileSubject.next(null);
      });

    this.oauthService.events
      .pipe(filter((e: OAuthEvent) => e.type === 'session_terminated'))
      .subscribe(() => {
        this.logout();
    });

    this.oauthService.events
      .pipe(filter((e: OAuthEvent) => e.type === 'token_refresh_error'))
      .subscribe(() => {
        console.error('Chyba pri obnove tokenu');
        this.logout();
      });
  }

  /**
   * Zahaajenie procesu prihlasovania
   * Presmeruje pouzivatela na IAM prihlasenie
   */
  public async login(targetUrl?: string, provider: AuthProvider = 'default') {
    console.log('Zahajujem prihlasovanie...');
    console.log('Provider:', provider);
    console.log('Target URL:', targetUrl || 'default');

    // Save provider to sessionStorage so we can restore it after the redirect
    sessionStorage.setItem('auth_provider', provider);

    // Reconfigure OAuth service if using a non-default provider
    if (provider !== 'default') {
      console.log('Reconfiguring OAuth service for provider:', provider);
      const newConfig = getAuthConfig(provider);
      console.log('New issuer:', newConfig.issuer);
      console.log('New client ID:', newConfig.clientId);
      this.oauthService.configure(newConfig);

      try {
        console.log('Loading discovery document for provider:', provider);
        await this.oauthService.loadDiscoveryDocument();
        console.log('Discovery document loaded for provider:', provider);
      } catch (err) {
        console.error('Error loading discovery document for provider:', provider, err);
        // Continue anyway - initCodeFlow will attempt to load it
      }
    } else {
      // Wait for discovery document to load before attempting login
      if (this.discoveryDocumentLoaded) {
        console.log('Cakam na nacitanie discovery dokumentu...');
        await this.discoveryDocumentLoaded;
        console.log('Discovery dokument nacitany, pokracujem s prihlasenim');
      }
    }

    console.log('Config redirect URI:', this.oauthService.redirectUri);
    
    try {
      if (targetUrl) {
        this.oauthService.initCodeFlow(targetUrl);
      } else {
        this.oauthService.initCodeFlow();
      }
      console.log('Prihlasovanie iniciovane, presmerovavam na IAM...');
    } catch (err) {
      console.error('Chyba pri zahajeni prihlasovania:', err);
      alert('Nepodarilo sa pripojit k prihlasovaciemu serveru. Skontrolujte internetove pripojenie.');
    }
  }

  /**
   * Wait for the initial authentication check to complete
   * This should be called before checking auth status on app init
   */
  public async waitForAuthenticationComplete(): Promise<void> {
    if (this.discoveryDocumentLoaded) {
      await this.discoveryDocumentLoaded;
    }
  }

  /**
   * Handle OAuth callback - process authorization code and exchange for tokens
   * This should be called from the callback component
   */
  public async handleCallback(): Promise<void> {
    console.log('🔄 handleCallback: Starting callback processing...');
    
    try {
      // Make sure discovery document is loaded first
      await this.waitForAuthenticationComplete();
      
      // Try to login - this will process the code from URL and exchange it for tokens
      console.log('Attempting to process authorization code...');
      const success = await this.oauthService.tryLogin();
      
      console.log('tryLogin result:', success);
      console.log('hasValidAccessToken:', this.oauthService.hasValidAccessToken());
      console.log('hasValidIdToken:', this.oauthService.hasValidIdToken());
      
      if (this.oauthService.hasValidAccessToken()) {
        console.log('✅ Token exchange successful!');
        this.isAuthenticatedSubject.next(true);
        this.loadUserProfile();
      } else {
        console.error('❌ Token exchange failed - no valid access token');
        
        // Check for errors in URL
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('error')) {
          console.error('OAuth Error:', {
            error: urlParams.get('error'),
            error_description: urlParams.get('error_description')
          });
        }
      }
    } catch (error) {
      console.error('❌ Error during callback processing:', error);
      throw error;
    }
  }

  /**
   * Silent login for webview with injected SSO cookie
   * Uses prompt=none to attempt automatic authentication via KEYCLOAK_IDENTITY cookie
   * This will redirect the browser, so no return value is meaningful
   */
  public trySilentLogin(): void {
    console.log('Attempting silent login with SSO cookie...');
    
    // Check if already authenticated
    if (this.isLoggedIn()) {
      console.log('Already authenticated, skipping silent login');
      return;
    }

    // Initiate silent login with prompt=none
    // This will redirect to Keycloak /auth endpoint with prompt=none
    // The browser will be redirected, then Keycloak will redirect back with auth code
    // The OAuth library will automatically handle the callback
    console.log('Initiating code flow with prompt=none - browser will redirect');
    this.oauthService.initCodeFlow(undefined, { prompt: 'none' });
    
    // Note: Code after this line will NOT execute because the browser redirects
  }

  /**
   * Check if app is running in a webview (SmartBanking)
   * Detects based on URL parameters or specific webview markers
   */
  public isRunningInWebview(): boolean {
    // Check for webview parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const isWebview = urlParams.get('webview') === 'true' || 
                      urlParams.get('mode') === 'webview' ||
                      urlParams.get('embedded') === 'true';
    
    // Check for specific user agent markers if needed
    const userAgent = navigator.userAgent.toLowerCase();
    const hasWebviewUA = userAgent.includes('webview') || 
                         userAgent.includes('smartbanking');
    
    const result = isWebview || hasWebviewUA;
    console.log('Webview detection:', result, { urlParams: Object.fromEntries(urlParams), userAgent });
    
    return result;
  }

  /**
   * Odhlasenie pouzivatela
   */
  public logout() {
    // Prevent silent login from immediately re-authenticating after logout
    sessionStorage.setItem('just_logged_out', 'true');
    this.oauthService.logOut();
    this.isAuthenticatedSubject.next(false);
    this.userProfileSubject.next(null);
    this.router.navigate(['/']);
  }

  /**
   * Kontrola ci je pouzivatel prihlaseny
   * Kontroluje platnost access tokenu, ID tokenu a ci token neexpiroval
   */
  public isLoggedIn(): boolean {
    const hasValidTokens = this.oauthService.hasValidAccessToken() && 
                          this.oauthService.hasValidIdToken();
    
    if (!hasValidTokens) {
      return false;
    }
    
    // Additional expiration check
    if (this.isTokenExpired()) {
      console.log('Token has expired');
      return false;
    }
    
    return true;
  }

  /**
   * Ziskanie access tokenu pre API volania
   */
  public getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  /**
   * Ziskanie ID tokenu
   */
  public getIdToken(): string {
    return this.oauthService.getIdToken();
  }

  /**
   * Nacitanie profilu pouzivatela z ID tokenu
   */
  private loadUserProfile() {
    const claims = this.oauthService.getIdentityClaims();
    if (claims) {
      this.userProfileSubject.next(claims as UserProfile);
    }
  }

  /**
   * Ziskanie profilu pouzivatela
   */
  public getUserProfile(): UserProfile | null {
    return this.userProfileSubject.value;
  }

  /**
   * Ziskanie mena pouzivatela
   */
  public getUserName(): string {
    const profile = this.getUserProfile();
      const givenName = profile?.given_name?.trim() || '';
      const familyName = profile?.family_name?.trim() || '';
      const fullName = `${givenName} ${familyName}`.trim();

      return fullName ||
        profile?.name ||
        profile?.preferred_username ||
        profile?.email ||
        'Používateľ';
  }

  /**
   * Ziskanie login/sub pouzivatela z tokenu
   * Pouziva sa ako login parameter pri API volaniach
   */
  public getUserLogin(): string | null {
    const profile = this.getUserProfile();
    return profile?.sub || null;
  }

  /**
   * Ziskanie eDesk URI pouzivatela z tokenu.
   * Pouziva sa ako x-edesk-uri header pri API volaniach.
   * Hlada v beznych claim nazvoch; vrati null ak nie je dostupna.
   */
  public getEdeskUri(): string | null {
    const profile = this.getUserProfile();
    if (!profile) return null;
    return profile['edesk_uri']
      ?? profile['edeskUri']
      ?? profile['edesk-uri']
      ?? profile['edesk_id']
      ?? null;
  }

  /**
   * Try to get new tokens using SSO session (prompt=none)
   * This will redirect the browser
   */
  public async refreshToken(): Promise<void> {
    console.log('Attempting to get new tokens using SSO session...');
    this.trySilentLogin();
  }

  /**
   * Ziskanie casu do expirace tokenu (v sekundach)
   */
  public getTokenExpirationTime(): number | null {
    const expiresAt = this.oauthService.getAccessTokenExpiration();
    if (!expiresAt) return null;
    
    const now = Date.now();
    return Math.max(0, Math.floor((expiresAt - now) / 1000));
  }

  /**
   * Kontrola ci token expiroval
   */
  public isTokenExpired(): boolean {
    const expiresAt = this.oauthService.getAccessTokenExpiration();
    if (!expiresAt) return true;
    
    return Date.now() >= expiresAt;
  }

  /**
   * Zaciatok periodickeho monitorovania expirace tokenu
   * Only monitors and logs - doesn't automatically refresh
   * SmartBanking app maintains the SSO session
   */
  private startTokenExpirationCheck(): void {
    // Clear any existing interval
    if (this.tokenExpirationCheckInterval) {
      clearInterval(this.tokenExpirationCheckInterval);
    }

    // Check every 60 seconds
    this.tokenExpirationCheckInterval = setInterval(() => {
      // Check if we have tokens first
      if (!this.oauthService.hasValidAccessToken()) {
        return;
      }

      const timeLeft = this.getTokenExpirationTime();
      const expiresAt = this.oauthService.getAccessTokenExpiration();
      const now = new Date();
      const expiresAtDate = expiresAt ? new Date(expiresAt) : null;
      
      // Only log if token is expiring soon (less than 5 minutes) or expired
      if (timeLeft !== null && timeLeft <= 300) {
        console.log(`[Token Check] Time left: ${timeLeft}s | Current: ${now.toLocaleTimeString()} | Expires: ${expiresAtDate?.toLocaleTimeString()}`);
      }
      
      // Token expired - let API calls handle re-authentication
      if (timeLeft === 0) {
        console.log('Access token expired. Will re-authenticate on next API call.');
        // Don't logout - let the app continue and handle it gracefully
        // The auth guard and interceptor will trigger re-authentication when needed
      }
    }, 60000); // Check every 60 seconds
  }

  /**
   * Zastavenie monitorovania expirace tokenu
   */
  private stopTokenExpirationCheck(): void {
    if (this.tokenExpirationCheckInterval) {
      clearInterval(this.tokenExpirationCheckInterval);
      this.tokenExpirationCheckInterval = null;
    }
  }

  /**
   * Cleanup pri destruction servisu
   */
  ngOnDestroy(): void {
    this.stopTokenExpirationCheck();
  }
}
